import { useEffect, useMemo, useState } from 'react';
import { byPcName } from '../services/dispatchPick';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import { useQueueAffordances } from '../hooks/useQueueAffordances';
import { formatEta, fullName, partyMarker } from '../services/queue';
import LanguageFlag from './LanguageFlag';
import QueueManagePanel from './QueueManagePanel';
import QueuePickerModal from './QueuePickerModal';
import BookingOutlook from './BookingOutlook';
import { NEXT_UP_EVENT, type NextUpSignal } from './NextUpWidget';
import type { QueueEntry } from '../types/database';

// The queue column of the queue-mode dashboard (design/Main.dc.html, right
// half): how many are waiting, who to prepare right now and on which PC, then
// the rest of the line.
//
// This is where the NextUpWidget's affordances live in queue mode — Beheer
// (QueueManagePanel), undo-with-confirm, park/unpark and the pending-move
// banner all come from hooks/useQueueAffordances, so there is one
// implementation rather than two surfaces drifting apart.

/** How many "now prepare this person" pairings to show (design shows two). */
const PREP_COUNT = 2;
/** How many of the remaining queue rows to list before "+ nog N wachtenden". */
const REST_ROWS = 6;

interface PcFreeSlot {
  pcId: string;
  pcName: string;
  /** What the bar for this PC shows, so the pairing can be matched to it. */
  pcLabel: string;
  /** 0 = free right now, N = frees in about N minutes, null = busy, unknown. */
  freeInMinutes: number | null;
}

function shortExperience(names: string | null | undefined): string {
  const s = (names ?? '').trim();
  return s || '—';
}

export default function QueuePanel() {
  const queue = useQueueDequeue();
  const dispatch = useQueueDispatchOptional();
  const {
    busy, note, setNote, undoable, undoLabel, undoTitle, undoLast,
    togglePark, pendingMove, requeuePending, openMove,
  } = useQueueAffordances();
  const [manageOpen, setManageOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [nextUp, setNextUp] = useState<NextUpSignal | null>(null);

  // "vrij over ~N min" counts down against wall-clock time; nothing else in
  // the tree re-renders this panel on its own.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // The agent's "Bezoeker is bijna klaar" fan-out (PCTile broadcasts it on
  // last_level_warning). Auto-dismisses after 10s, like the widget's banner.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    function onSignal(e: CustomEvent<NextUpSignal>) {
      setNextUp(e.detail);
      if (t) clearTimeout(t);
      t = setTimeout(() => setNextUp(null), 10_000);
    }
    window.addEventListener(NEXT_UP_EVENT, onSignal);
    return () => {
      window.removeEventListener(NEXT_UP_EVENT, onSignal);
      if (t) clearTimeout(t);
    };
  }, []);

  const { entries, cycleMinutes, startedByPc, loading } = queue;
  const pcReports = dispatch?.pcReports ?? null;

  // --- The pairing. Real data, no guessing:
  //  * a PC is free NOW when its own dispatch report says online, not running
  //    and not recovering (the tile's 8s poll feeds that report);
  //  * a busy PC frees at its visitor's started_at + the queue payload's
  //    cycle_minutes — the same arithmetic booking.fn_queue_capacity does for
  //    free_in_minutes server-side.
  // PCs sort by when they free; the non-parked queue head pairs with them in
  // order, so the first person named is the one to prepare first.
  const slots: PcFreeSlot[] = useMemo(() => {
    if (!pcReports) return [];
    const now = Date.now();
    const out: PcFreeSlot[] = [];
    for (const [pcId, report] of pcReports) {
      if (!report.online) continue;
      if (!report.running && !report.recovering) {
        out.push({ pcId, pcName: report.pcName, pcLabel: report.pcLabel ?? report.pcName, freeInMinutes: 0 });
        continue;
      }
      const started = startedByPc.get(pcId);
      if (!started) {
        // Busy with somebody the queue does not know (walk-in, pre-queue run).
        out.push({ pcId, pcName: report.pcName, pcLabel: report.pcLabel ?? report.pcName, freeInMinutes: null });
        continue;
      }
      const startedAt = Date.parse(started.started_at);
      if (!Number.isFinite(startedAt)) {
        out.push({ pcId, pcName: report.pcName, pcLabel: report.pcLabel ?? report.pcName, freeInMinutes: null });
        continue;
      }
      const endsAt = startedAt + cycleMinutes * 60_000;
      out.push({
        pcId,
        pcName: report.pcName, pcLabel: report.pcLabel ?? report.pcName,
        freeInMinutes: Math.max(0, Math.round((endsAt - now) / 60_000)),
      });
    }
    // Unknown end times go last: a PC we cannot time should never outrank one
    // we can. Equal times break by name -- through the shared comparator, which
    // is natural-sorted, so ANDROMEDA-2 comes before ANDROMEDA-10. A plain
    // localeCompare here put 10 first while the pop-up put 2 first
    // (Dirk 2026-09-16).
    return out.sort((a, b) => {
      const am = a.freeInMinutes ?? Number.MAX_SAFE_INTEGER;
      const bm = b.freeInMinutes ?? Number.MAX_SAFE_INTEGER;
      return am !== bm ? am - bm : byPcName(a, b);
    });
    // `pcReports` is a fresh Map on every tile transition; the tick keeps the
    // minutes honest between transitions.
  }, [pcReports, startedByPc, cycleMinutes]);

  const waiting = entries.filter((e) => !e.parked);
  const prep = waiting.slice(0, PREP_COUNT).map((entry, i) => ({
    entry,
    slot: slots[i] ?? null,
  }));
  const prepIds = new Set(prep.map((p) => p.entry.queue_entry_id));
  const rest = entries.filter((e) => !prepIds.has(e.queue_entry_id));
  const restShown = rest.slice(0, REST_ROWS);
  const restHidden = rest.length - restShown.length;
  const lastEta = entries.length > 0 ? entries[entries.length - 1].eta_minutes : null;

  function freeText(slot: PcFreeSlot | null): string {
    if (!slot) return 'geen vrije PC';
    if (slot.freeInMinutes === null) return 'bezet';
    return slot.freeInMinutes <= 0 ? 'nu vrij' : `vrij over ~${slot.freeInMinutes} min`;
  }

  function renderRestRow(e: QueueEntry) {
    const marker = partyMarker(e);
    const eta = e.eta_minutes !== null && e.eta_minutes > 0 ? formatEta(e.eta_minutes) : '';
    return (
      <div key={e.queue_entry_id} className={`qq-row${e.parked ? ' qq-row--parked' : ''}`}>
        <div className="qq-row-pos">{e.position}</div>
        <div className="qq-row-who">
          <span className="qq-row-name">{fullName(e)}</span>
          <LanguageFlag code={e.play_language} className="qq-flag" />
          {marker ? <span className="qq-row-party">{marker}</span> : null}
        </div>
        <span className="qq-spacer" />
        {e.parked ? (
          <span className="queue-parked-pill">even weg</span>
        ) : (
          <>
            <span className="qq-row-exp">{shortExperience(e.experience_names)}</span>
            <span className="qq-row-eta">{eta}</span>
          </>
        )}
        {/* Park is an explicit control, not a click on the whole row: on a
            kiosk touchscreen a stray tap must never silently skip somebody. */}
        <button
          type="button"
          className="qq-row-park"
          disabled={busy}
          onClick={() => void togglePark(e)}
          aria-label={e.parked ? 'Terug in de rij' : 'Even weg'}
          title={e.parked
            ? 'Bezoeker is terug: weer aan de beurt op de eigen plek.'
            : 'Bezoeker is even weg (toilet, kleedkamer): houdt de plek, wordt overgeslagen tot Terug.'}
        >
          {e.parked ? '↺' : '⏸'}
        </button>
      </div>
    );
  }

  return (
    <div className="qq-panel">
      <div className="qq-head">
        <span className="qq-head-title">WACHTRIJ</span>
        <span className="qq-head-sub">
          {loading && entries.length === 0
            ? 'laden…'
            : `${entries.length} wachtende${entries.length === 1 ? '' : 'n'}${
                lastEta !== null && lastEta > 0 ? ` · ~${lastEta} min voor de laatste` : ''
              }`}
        </span>
        <span className="qq-spacer" />
        <span className="qq-head-actions">
          {dispatch && waiting.length > 0 ? (
            <button
              type="button"
              className="qq-link qq-link--go"
              title="Open het 'Volgende bezoeker' venster: kies een vrije PC en start de geboekte ervaring."
              onClick={() => dispatch.openDispatch()}
            >
              ▶ Starten
            </button>
          ) : null}
          {waiting.length > 0 ? (
            <button
              type="button"
              className="qq-link"
              title="Kies handmatig wie er als volgende uit de wachtrij gaat."
              onClick={() => setPickerOpen(true)}
            >
              Bewerken
            </button>
          ) : null}
          <button
            type="button"
            className="qq-link"
            title="Wachtrij beheren: volgorde, verwijderen, toevoegen, ongedaan maken."
            onClick={() => setManageOpen(true)}
          >
            Beheer
          </button>
          {undoable ? (
            <button
              type="button"
              className="qq-link qq-link--undo"
              disabled={busy}
              title={undoTitle}
              onClick={() => void undoLast()}
            >
              {busy ? 'Bezig…' : undoLabel}
            </button>
          ) : null}
        </span>
      </div>

      {/* A visitor stopped for a move who has not landed anywhere yet. */}
      {pendingMove && dispatch ? (
        <div className="qq-banner qq-banner--move" role="status">
          <span className="qq-banner-label">Verplaatsen:</span>
          <span className="qq-banner-text">
            <strong>{fullName(pendingMove.entry)}</strong> gestopt op {pendingMove.fromPcLabel}.
            {' '}Kies een andere PC of zet de bezoeker terug in de wachtrij.
          </span>
          <button type="button" className="qq-banner-btn" disabled={busy} onClick={openMove}>
            Verplaatsen
          </button>
          <button type="button" className="qq-banner-btn" disabled={busy} onClick={() => void requeuePending()}>
            Terug in wachtrij
          </button>
        </div>
      ) : null}

      {note ? (
        <div className="qq-banner" role="status" onClick={() => setNote(null)}>
          <span className="qq-banner-label">Wachtrij:</span>
          <span className="qq-banner-text">{note}</span>
        </div>
      ) : null}

      {nextUp ? (
        <div className="qq-banner qq-banner--next" role="status">
          <span className="qq-banner-label">Bijna klaar op {nextUp.pcName}.</span>
          <span className="qq-banner-text">
            Volgende: <strong>{nextUp.displayName}</strong>, naar voren halen.
          </span>
          <button
            type="button"
            className="qq-banner-close"
            onClick={() => setNextUp(null)}
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>
      ) : null}

      {/* Next up: the pairing that tells staff which headset to prep */}
      {prep.length > 0 ? (
        <div className="qq-prep">
          <div className="qq-prep-label">NU KLAARMAKEN</div>
          {prep.map(({ entry, slot }, i) => (
            <div className="qq-prep-row" key={entry.queue_entry_id}>
              <div className={`qq-prep-num${i === 0 ? ' qq-prep-num--now' : ''}`}>{i + 1}</div>
              <div className="qq-prep-who">
                <div className="qq-prep-name-line">
                  <span className="qq-prep-name">{fullName(entry)}</span>
                  <LanguageFlag code={entry.play_language} className="qq-flag" />
                  {partyMarker(entry) ? <span className="qq-row-party">{partyMarker(entry)}</span> : null}
                </div>
                <div className="qq-prep-exp">{shortExperience(entry.experience_names)}</div>
              </div>
              <span className="qq-spacer" />
              <div className="qq-prep-pc">
                <div className={`qq-prep-pc-name${slot && slot.freeInMinutes === 0 ? ' qq-prep-pc-name--now' : ''}`}>
                  {slot ? slot.pcLabel : '—'}
                </div>
                <div className="qq-prep-pc-when">{freeText(slot)}</div>
              </div>
              {dispatch && slot && slot.freeInMinutes === 0 ? (
                <button
                  type="button"
                  className="qbar-btn qbar-btn--go qq-prep-start"
                  title={`Start ${fullName(entry)} op ${slot.pcName}.`} /* title keeps the machine name: it is the tooltip, not the glance */
                  onClick={() => dispatch.openDispatch(slot.pcId)}
                >
                  Start
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* The rest of the line */}
      <div className="qq-list">
        {entries.length === 0 ? (
          <div className="qq-empty">{loading ? 'Wachtrij laden…' : 'Wachtrij is leeg'}</div>
        ) : (
          <>
            {restShown.map(renderRestRow)}
            {restHidden > 0 ? (
              <div className="qq-more">+ nog {restHidden} wachtende{restHidden === 1 ? '' : 'n'}</div>
            ) : null}
          </>
        )}
      </div>

      {/* What is BOOKED, under who is WAITING. They compete for the same
          stations, so the desk reads them together: the queue answers "who is
          next", this answers "can I still say yes to a walk-in". */}
      <BookingOutlook locationId={queue.locationId} />

      {pickerOpen ? <QueuePickerModal onClose={() => setPickerOpen(false)} /> : null}
      {manageOpen ? <QueueManagePanel onClose={() => setManageOpen(false)} /> : null}
    </div>
  );
}
